/**
 * BuilderCanvas Component
 * 
 * Main drag-and-drop canvas for arranging blocks.
 * Uses dnd-kit for smooth drag-and-drop functionality.
 */

import React, { memo, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useMenuBuilderStore, useSortedBlocks } from '@/stores/menuBuilderStore';
import { BlockRenderer } from '../blocks/BlockRenderer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus } from 'lucide-react';
import type { Branch } from '@/types/index';

import { useGlobalThemeStyles } from '@/hooks/useGlobalThemeStyles';
import { MenuThemeProvider } from '@/contexts/MenuThemeContext';
import { overridesToCustomization } from '@/utils/menuCustomizationConverter';
import { FloatingCart } from '@/components/menu/FloatingCart';
import { useFilteredBranches } from '@/hooks/useFilteredBranches';
import { useBranch } from '@/contexts/BranchContext';
import { useMemo } from 'react';
import { FontLoader } from '@/components/common/FontLoader';

interface BuilderCanvasProps {
  className?: string;
}

export const BuilderCanvas = memo(function BuilderCanvas({
  className,
}: BuilderCanvasProps) {
  const { t } = useTranslation();
  const blocks = useSortedBlocks();
  const moveBlock = useMenuBuilderStore((s) => s.moveBlock);
  const selectBlock = useMenuBuilderStore((s) => s.selectBlock);
  const setDragging = useMenuBuilderStore((s) => s.setDragging);
  const viewMode = useMenuBuilderStore((s) => s.viewMode);
  const globalTheme = useMenuBuilderStore((s) => s.globalThemeOverrides);
  const backgroundStyle = useGlobalThemeStyles(globalTheme);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);

  // Load selected font
  const fontFamily = globalTheme?.fontFamily;

  const { selectedBranchId } = useBranch();
  const { branches } = useFilteredBranches();
  const currentBranch = branches.find(b => b.id === selectedBranchId);

  const customizationOverride = useMemo(() => 
    overridesToCustomization(globalTheme || {}), 
    [globalTheme]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setActiveId(event.active.id as string);
      setDragging(true, event.active.id as string);
    },
    [setDragging]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      setDragging(false, null);

      if (over && active.id !== over.id) {
        const oldIndex = blocks.findIndex((b) => b.id === active.id);
        const newIndex = blocks.findIndex((b) => b.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          moveBlock(oldIndex, newIndex);
        }
      }
    },
    [blocks, moveBlock, setDragging]
  );

  const handleCanvasClick = useCallback(() => {
    selectBlock(null);
  }, [selectBlock]);

  // Device frame dimensions based on view mode
  const deviceWidth = viewMode === 'mobile' ? 'max-w-[375px]' : 'max-w-[768px]';

  return (
    <div
      className={cn(
        'flex-1 bg-muted/30 flex items-start justify-center p-8 overflow-auto',
        className
      )}
      onClick={handleCanvasClick}
    >
      <FontLoader fontFamily={fontFamily} />
      {/* Device Frame */}
      <div
        className={cn(
          'bg-background rounded-2xl shadow-lg border w-full transition-all duration-300 relative',
          deviceWidth
        )}
        style={backgroundStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Device Header (status bar simulation) */}
        <div className="h-6 bg-muted rounded-t-2xl flex items-center justify-center">
          <div className="w-16 h-1 bg-muted-foreground/30 rounded-full" />
        </div>

        {/* Canvas Content */}
        <ScrollArea ref={scrollAreaRef} className="h-[600px]">
          <div className="p-4 min-h-full">
            {blocks.length === 0 ? (
              // Empty state
              <div className="h-[500px] flex flex-col items-center justify-center text-center text-muted-foreground border-2 border-dashed rounded-lg">
                <Plus className="h-12 w-12 mb-4 opacity-50" />
                <h3 className="font-medium">
                  {t('menuBuilder.emptyCanvas', 'Start Building Your Menu')}
                </h3>
                <p className="text-sm mt-2 max-w-xs">
                  {t(
                    'menuBuilder.emptyCanvasHint',
                    'Click a block from the toolbox to add it here'
                  )}
                </p>
              </div>
            ) : (
              // Block list with drag-and-drop
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={blocks.map((b) => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-4 pt-10">
                    {blocks.map((block) => (
                      <div key={block.id} id={`block-${block.id}`}>
                        <BlockRenderer block={block} />
                      </div>
                    ))}
                  </div>
                </SortableContext>
                <DragOverlay>
                  {activeId ? (
                    <div className="opacity-90 rotate-3 scale-105">
                      <BlockRenderer 
                        block={blocks.find(b => b.id === activeId)!} 
                      />
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </div>
        </ScrollArea>

        {/* Floating Cart Preview */}
        {currentBranch && (
          <MenuThemeProvider branch={currentBranch as unknown as Branch} customizationOverride={customizationOverride}>
             <FloatingCart 
                branchId={currentBranch.id} 
                currency={currentBranch.currency || 'USD'} 
                isPreview={true} 
             />
          </MenuThemeProvider>
        )}

        {/* Device Footer (home indicator simulation) */}
        <div className="h-6 bg-muted rounded-b-2xl flex items-center justify-center">
          <div className="w-32 h-1 bg-muted-foreground/30 rounded-full" />
        </div>
      </div>
    </div>
  );
});

export default BuilderCanvas;
